import React, { useMemo } from "react";
import { Segment, Header, Statistic, List, Divider } from "semantic-ui-react";
import { formatPrice } from "../../../common/utils/format";

/**
 * 사용자 본인의 도서 신청 통계 패널.
 *
 * @param books — 사용자가 신청한 도서 목록 (전체). { reqYearMonth, status, price } 사용.
 *
 * 표시 내용:
 *   - 이번달: 총 신청 N권 / 총합 X원 / 승인 합계 Y원
 *   - 최근 6개월: 월별 권수 리스트 (최신 → 과거)
 */
const MyRequestStatsPanel = ({ books }) => {
  const stats = useMemo(() => computeStats(books || []), [books]);

  return (
    <Segment>
      <Header as="h4" style={{ marginBottom: "0.5em" }}>
        📊 내 신청 현황
      </Header>
      <Divider />

      <div style={{ marginBottom: "0.5em", fontWeight: 600, color: "#555" }}>
        이번달 ({stats.currentLabel})
      </div>
      <Statistic.Group size="mini" widths="three">
        <Statistic>
          <Statistic.Value>{stats.currentMonth.count}</Statistic.Value>
          <Statistic.Label>신청</Statistic.Label>
        </Statistic>
        <Statistic>
          <Statistic.Value style={{ fontSize: "1em" }}>
            {formatPrice(stats.currentMonth.totalPrice)}
          </Statistic.Value>
          <Statistic.Label>총합 (원)</Statistic.Label>
        </Statistic>
        <Statistic color="blue">
          <Statistic.Value style={{ fontSize: "1em" }}>
            {formatPrice(stats.currentMonth.approvedPrice)}
          </Statistic.Value>
          <Statistic.Label>승인 (원)</Statistic.Label>
        </Statistic>
      </Statistic.Group>

      <Divider />

      <div style={{ marginBottom: "0.5em", fontWeight: 600, color: "#555" }}>
        최근 6개월
      </div>
      {stats.pastMonths.length === 0 ? (
        <div style={{ color: "#999", fontSize: "0.9em" }}>이력 없음</div>
      ) : (
        <List divided relaxed="very" size="small">
          {stats.pastMonths.map(m => (
            <List.Item key={m.label}>
              <List.Content floated="right" style={{ color: "#2185d0" }}>
                {m.count} 권
              </List.Content>
              <List.Content>{m.label}</List.Content>
            </List.Item>
          ))}
        </List>
      )}
    </Segment>
  );
};

// reqYearMonth 는 "YYYYMM" 6자리 문자열.
const parseYM = ym => {
  if (!ym || ym.length < 6) return null;
  return { y: parseInt(ym.slice(0, 4), 10), m: parseInt(ym.slice(4, 6), 10) };
};

const computeStats = books => {
  const now = new Date();
  const curY = now.getFullYear();
  const curM = now.getMonth() + 1;
  const curYm = curY * 100 + curM;

  // 6개월 전 YYYYMM (포함). 예: 2026-05 면 2025-11.
  const sixMonthsAgo = (() => {
    let y = curY;
    let m = curM - 6;
    while (m <= 0) {
      y -= 1;
      m += 12;
    }
    return y * 100 + m;
  })();

  let currentCount = 0;
  let currentTotal = 0;
  let currentApproved = 0;
  const pastMap = new Map(); // ym -> count

  for (const b of books) {
    const p = parseYM(b.reqYearMonth);
    if (!p) continue;
    const ym = p.y * 100 + p.m;
    const price = Number(b.price) || 0;

    if (ym === curYm) {
      currentCount += 1;
      currentTotal += price;
      if (b.status === "APPROVAL" || b.status === "RECEIPT") {
        currentApproved += price;
      }
    } else if (ym >= sixMonthsAgo && ym < curYm) {
      pastMap.set(ym, (pastMap.get(ym) || 0) + 1);
    }
  }

  const pastMonths = Array.from(pastMap.entries())
    .sort((a, b) => b[0] - a[0]) // 최신부터
    .map(([ym, count]) => {
      const y = Math.floor(ym / 100);
      const m = ym % 100;
      return { label: `${y}년 ${m}월`, count };
    });

  return {
    currentLabel: `${curY}년 ${curM}월`,
    currentMonth: {
      count: currentCount,
      totalPrice: currentTotal,
      approvedPrice: currentApproved
    },
    pastMonths
  };
};

export default MyRequestStatsPanel;
